import { useTranslation } from 'react-i18next';
import { type Control, type UseFormSetValue } from 'react-hook-form';
import { AlertTriangle, RotateCcw } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { ReceiveConfirmContextDto } from '@/entities/receive';

import { supportsHandlingUnits } from '../lib/quantities';
import { CONFIRM_ARRIVAL_UNIT_MODE } from '../model/constants';
import type { ConfirmArrivalFormValues, ConfirmArrivalQuantityField, ConfirmArrivalUnitMode } from '../model/types';
import { ConfirmArrivalHandlingSummary } from './confirm-arrival-handling-summary';
import { ConfirmArrivalLineRow } from './confirm-arrival-line-row';

interface ConfirmArrivalLinesTableProps {
  control: Control<ConfirmArrivalFormValues>;
  setValue: UseFormSetValue<ConfirmArrivalFormValues>;
  context: ReceiveConfirmContextDto;
  unitMode: ConfirmArrivalUnitMode;
  onUnitModeChange: (mode: ConfirmArrivalUnitMode) => void;
  onResetQuantities: () => void;
}

export function ConfirmArrivalLinesTable({
  control,
  setValue,
  context,
  unitMode,
  onUnitModeChange,
  onResetQuantities
}: ConfirmArrivalLinesTableProps) {
  const { t } = useTranslation();

  // Losses are rejected outright for customer deliveries, so the whole column
  // goes — same as JSF, which hides it rather than disabling it.
  const showLostColumn = context.lostQtyAllowed;
  const anyBundleLines = context.lines.some(supportsHandlingUnits);
  const isHandlingMode = unitMode === CONFIRM_ARRIVAL_UNIT_MODE.HANDLING;

  const fieldLabels: Record<ConfirmArrivalQuantityField, string> = {
    goodQty: t('receive.goodquantity.label', 'Received in Good Condition'),
    damagedQty: t('receive.badquantity.label', 'Received in Damaged Condition'),
    lostQty: t('receive.lostintransit.label', 'Lost in Transit')
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {anyBundleLines ? (
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={unitMode}
            onValueChange={(value) => value && onUnitModeChange(value as ConfirmArrivalUnitMode)}
            aria-label={t('receive.confirm.unitMode.label', 'Quantity unit')}>
            <ToggleGroupItem value={CONFIRM_ARRIVAL_UNIT_MODE.INVENTORY}>
              {t('requestline.ltuQty.short', 'Inventory Units')}
            </ToggleGroupItem>
            <ToggleGroupItem value={CONFIRM_ARRIVAL_UNIT_MODE.HANDLING}>
              {t('bundle.bundleQty.label', 'Handling Units')}
            </ToggleGroupItem>
          </ToggleGroup>
        ) : (
          <span />
        )}

        <Button type="button" variant="ghost" size="sm" onClick={onResetQuantities}>
          <RotateCcw className="mr-2 h-4 w-4" />
          {t('dispatch.resetqtys.button', 'reset quantities')}
        </Button>
      </div>

      {isHandlingMode && (
        <Alert variant="warning">
          <AlertTriangle />
          <AlertDescription>
            {t(
              'disposition.bundlewarn.desc',
              'Warning! The displayed quantities are informative and might be not accurate (quantities in handling units are estimated based on the consignment line configuration). For exact quantities please switch back to Inventory units!'
            )}
          </AlertDescription>
        </Alert>
      )}

      <ConfirmArrivalHandlingSummary control={control} lines={context.lines} />

      <div className="overflow-hidden rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-60">{t('receive.requestline.label', 'Consignment Line')}</TableHead>
              <TableHead className="min-w-32.5">{t('receive.expectedquantity.label', 'Quantity Expected')}</TableHead>
              <TableHead className="min-w-42.5">{fieldLabels.goodQty}</TableHead>
              <TableHead className="min-w-42.5">{fieldLabels.damagedQty}</TableHead>
              {showLostColumn && <TableHead className="min-w-42.5">{fieldLabels.lostQty}</TableHead>}
              <TableHead className="min-w-32.5">{t('receive.remainingquantity.label', 'Remaining Quantity')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {context.lines.map((line, index) => (
              <ConfirmArrivalLineRow
                key={line.lineId}
                control={control}
                setValue={setValue}
                line={line}
                index={index}
                unitMode={unitMode}
                showLostColumn={showLostColumn}
                fieldLabels={fieldLabels}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
